<script setup lang="ts">
import { computed } from 'vue'

import { formatPercent } from '@/shared/lib'

/**
 * A change against an earlier period, as the backend's `…Percent` fields carry it: `12.5`
 * is `↑ 12.5%` in green, `-5` is `↓ 5%` in red, `0` is a plain `0%`.
 *
 * The arrow carries the sign, so the number beside it is always the distance — pass the
 * value as it arrives and do not take its absolute value first, or an increase and a fall
 * look the same. Nothing at all is drawn for `null`: a period the backend had no base to
 * compare against is not a 0% change.
 */
const props = defineProps<{
  value?: number | null
}>()

const tone = computed(() => {
  if (props.value === null || props.value === undefined) return null

  return props.value > 0 ? 'up' : props.value < 0 ? 'down' : 'flat'
})

const label = computed(() => {
  if (props.value === null || props.value === undefined) return ''

  const arrow = tone.value === 'up' ? '↑ ' : tone.value === 'down' ? '↓ ' : ''

  return `${arrow}${formatPercent(Math.abs(props.value))}`
})
</script>

<template>
  <span v-if="tone" class="delta-percent" :class="`delta-percent--${tone}`">
    {{ label }}
  </span>
</template>

<style lang="scss" scoped>
.delta-percent {
  white-space: nowrap;

  &--up {
    color: var(--el-color-success);
  }

  &--down {
    color: var(--el-color-danger);
  }
}
</style>
